import React, { useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Utility function to convert SVG to canvas for PNG export
const svgToCanvas = (
  svg: SVGElement,
  format: 'png',
  callback: (canvas: HTMLCanvasElement) => void
) => {
  try {
    // Get the parent container to determine actual rendered dimensions
    const parent = svg.parentElement;
    let width = 400;
    let height = 400;
    
    // Method 1: Try to get from parent container dimensions (most accurate for rendered charts)
    if (parent) {
      const parentRect = parent.getBoundingClientRect();
      if (parentRect.width > 0 && parentRect.height > 0) {
        width = parentRect.width;
        height = parentRect.height;
      }
    }
    
    // Method 2: Try to get from SVG's own bounding client rect
    if (width === 400 && height === 400) {
      const svgRect = svg.getBoundingClientRect();
      if (svgRect.width > 0 && svgRect.height > 0) {
        width = svgRect.width;
        height = svgRect.height;
      }
    }
    
    // Method 3: Try to get from viewBox
    if (width === 400 && height === 400) {
      const viewBox = svg.getAttribute('viewBox');
      if (viewBox) {
        const coords = viewBox.split(' ').map(Number);
        if (coords.length === 4) {
          width = coords[2] - coords[0];
          height = coords[3] - coords[1];
        }
      }
    }
    
    // Method 4: Try to get from explicit width/height attributes
    if (width === 400 && height === 400) {
      const widthAttr = svg.getAttribute('width');
      const heightAttr = svg.getAttribute('height');
      if (widthAttr) width = parseInt(widthAttr) || width;
      if (heightAttr) height = parseInt(heightAttr) || height;
    }
    
    // High scale factor for better quality (increased to 4 for even better quality)
    const scaleFactor = 4;
    
    // Serialize SVG data
    let svgData = new XMLSerializer().serializeToString(svg);
    
    // Ensure the SVG has proper width and height attributes
    if (!svg.getAttribute('width') || !svg.getAttribute('height')) {
      // Create a new SVG string with explicit dimensions
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgData, 'image/svg+xml');
      const svgElement = svgDoc.documentElement;
      
      if (!svgElement.getAttribute('width')) {
        svgElement.setAttribute('width', width.toString());
      }
      if (!svgElement.getAttribute('height')) {
        svgElement.setAttribute('height', height.toString());
      }
      
      svgData = new XMLSerializer().serializeToString(svgElement);
    }
    
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          console.error('Could not get canvas context');
          URL.revokeObjectURL(svgUrl);
          return;
        }

        // Set canvas dimensions with high scaling for better quality
        canvas.width = width * scaleFactor;
        canvas.height = height * scaleFactor;
        
        // Scale the context for high DPI
        ctx.scale(scaleFactor, scaleFactor);
        
        // Fill background with white to avoid transparent backgrounds
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);
        
        // Draw the image with explicit dimensions
        ctx.drawImage(img, 0, 0, width, height);

        URL.revokeObjectURL(svgUrl);
        callback(canvas);
      } catch (error) {
        console.error('Error in image onload handler:', error);
        URL.revokeObjectURL(svgUrl);
      }
    };

    img.onerror = () => {
      console.error('Error loading SVG image');
      URL.revokeObjectURL(svgUrl);
    };

    img.src = svgUrl;
  } catch (error) {
    console.error('Error in svgToCanvas:', error);
  }
};

export type ChartType = 'bar' | 'line' | 'pie' | 'area';

// Extended to match recharts data format requirements
interface ChartData {
  [key: string]: string | number;
  label: string;
  value: number;
}

// Type for chart data validation
interface ValidatedChartData {
  [key: string]: string | number;
  label: string;
  value: number;
}

interface GraphRendererProps {
  type: ChartType;
  title: string;
  data: ChartData[];
  xAxis?: string;
  yAxis?: string;
  onDownload?: (format: 'png' | 'svg' | 'pdf') => void;
}

const COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#10b981',
  '#f59e0b',
  '#ef4444'
];

// Utility function to validate and format chart data
const validateChartData = (data: ChartData[]): ValidatedChartData[] => {
  return data.map(item => ({
    label: String(item.label || ''),
    value: typeof item.value === 'number' ? item.value : 0
  })).filter(item => item.label !== '' || item.value !== 0);
};

const GraphRenderer: React.FC<GraphRendererProps> = ({
  type,
  title,
  data,
  xAxis = 'X-Axis',
  yAxis = 'Y-Axis',
  onDownload
}) => {
  console.log('GraphRenderer props:', { type, title, data, xAxis, yAxis });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const validatedData = validateChartData(data);
  
  console.log('GraphRenderer validatedData:', validatedData);

  // Check if data is valid
  const hasValidData = validatedData.length > 0 &&
    validatedData.some(item => item.value !== 0 || item.label !== '');

  /* =======================
     Download handler
  ======================= */

  const handleDownload = (format: 'png' | 'svg' | 'pdf') => {
    if (onDownload) {
      onDownload(format);
      return;
    }

    // Wait a bit longer for the chart to fully render before trying to download
    setTimeout(() => {
      if (format === 'svg' && containerRef.current) {
        // For SVG download, we can still use the SVG element
        let svgElement: SVGElement | null = null;
        const svg = containerRef.current?.querySelector('div.recharts-wrapper svg') || 
                    containerRef.current?.querySelector('svg');
        
        // Check if the found element is an SVGElement
        if (svg instanceof SVGElement) {
          svgElement = svg;
        }
        
        if (!svgElement) {
          console.warn('No SVG element found for download');
          // Try to find any SVG in the container as a last resort
          const svgs = containerRef.current?.querySelectorAll('svg');
          if (svgs && svgs.length > 0) {
            // Use the first SVG found that is an SVGElement
            for (let i = 0; i < svgs.length; i++) {
              if (svgs[i] instanceof SVGElement) {
                svgElement = svgs[i] as SVGElement;
                console.log('Found SVG using fallback method');
                break;
              }
            }
          }
          
          if (!svgElement) {
            console.warn('No SVG elements found in container');
            return;
          }
        }

        // Add a small additional delay to ensure the SVG is fully rendered
        setTimeout(() => {
          if (svgElement) {
            downloadSvg(svgElement, format);
          }
        }, 50);
      } else if (containerRef.current) {
        // For PNG and PDF downloads, capture the entire container
        // For SVG, we already handled it above
        if (format !== 'svg' && containerRef.current) {
          // Add a small additional delay to ensure everything is fully rendered
          setTimeout(() => {
            downloadContainer(containerRef.current as HTMLDivElement, format);
          }, 100);
        }
      }
    }, 200); // Increased delay to ensure chart is fully rendered
  };

  const downloadContainer = async (container: HTMLDivElement, format: 'png' | 'pdf') => {
    try {
      // Use html2canvas to capture the entire container
      const canvas = await html2canvas(container, {
        scale: 2, // Increase scale for better quality
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: container.scrollWidth,
        height: container.scrollHeight
      });

      if (format === 'pdf') {
        // PDF download using jsPDF
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('l', 'px', [canvas.width, canvas.height]);
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`${title.replace(/\s+/g, '_')}.pdf`);
      } else {
        // PNG download
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/\s+/g, '_')}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error(`Error downloading chart container as ${format}:`, error);
    }
  };

  const downloadSvg = (svg: SVGElement, format: 'png' | 'svg' | 'pdf') => {
    try {
      if (format === 'svg') {
        // SVG download
        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/\s+/g, '_')}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      } else if (format === 'pdf') {
        // PDF download using jsPDF
        svgToCanvas(svg, 'png', (canvas) => {
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('l', 'px', [canvas.width, canvas.height]);
          pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
          pdf.save(`${title.replace(/\s+/g, '_')}.pdf`);
        });
      } else {
        // PNG download using canvas
        svgToCanvas(svg, format, (canvas) => {
          const url = canvas.toDataURL(`image/${format}`);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${title.replace(/\s+/g, '_')}.${format}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
      }
    } catch (error) {
      console.error(`Error downloading chart as ${format}:`, error);
    }
  };

  /* =======================
     Shared axis config
  ======================= */

  const commonXAxisProps = {
    dataKey: 'label',
    interval: 0,
    angle: -30,
    textAnchor: 'end' as const,
    height: 60
  };

  /* =======================
     Chart renderer
  ======================= */

  const renderChart = () => {
    // Show message if no valid data
    if (!hasValidData) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#666',
          fontSize: '14px'
        }}>
          No valid data available for chart
        </div>
      );
    }

    switch (type) {
      case 'bar':
        return (
          <BarChart data={validatedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...commonXAxisProps} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="value"
              name={yAxis}
              fill="#3b82f6"
              isAnimationActive
              animationDuration={600}
            />
          </BarChart>
        );

      case 'line':
        return (
          <LineChart data={validatedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...commonXAxisProps} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              name={yAxis}
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              isAnimationActive
              animationDuration={600}
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={validatedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...commonXAxisProps} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="value"
              name={yAxis}
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.2}
              isAnimationActive
              animationDuration={600}
            />
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={validatedData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={90}
              labelLine
              isAnimationActive
              animationDuration={600}
              label={({ name, percent }) => {
                if (percent === undefined || isNaN(percent)) {
                  return `${name}: 0%`;
                }
                return `${name}: ${(percent * 100).toFixed(1)}%`;
              }}
            >
              {validatedData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        );

      default:
        return null;
    }
  };

  // State to track if we should use fallback rendering
  const [useFallback, setUseFallback] = React.useState(false);

  // Fallback dimensions to prevent negative values
  const fallbackWidth = 400;
  const fallbackHeight = 400;

  // Check initial dimensions when component mounts
  useEffect(() => {
    const checkDimensions = () => {
      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
          setUseFallback(true);
        }
      }
    };

    // Check dimensions after a short delay to ensure rendering is complete
    const timer = setTimeout(checkDimensions, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px'
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          {title}
        </h3>

        <div>
          <button
            onClick={() => handleDownload('png')}
            aria-label="Download as PNG"
            style={{
              background: 'none',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '4px 8px',
              margin: '0 2px',
              cursor: 'pointer'
            }}
          >
            📷
          </button>
          {/* <button
            onClick={() => handleDownload('svg')}
            aria-label="Download as SVG"
            style={{
              background: 'none',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '4px 8px',
              margin: '0 2px',
              cursor: 'pointer'
            }}
          >
            📐 SVG
          </button> */}
          {/* <button
            onClick={() => handleDownload('pdf')}
            aria-label="Download as PDF"
            style={{
              background: 'none',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '4px 8px',
              margin: '0 2px',
              cursor: 'pointer'
            }}
          >
            📄 PDF
          </button> */}
        </div>
      </div>

      <div style={{ width: '100%', height: 400, minWidth: 0, minHeight: 0, position: 'relative' }}>
        {useFallback ? (
          <div style={{ width: fallbackWidth, height: fallbackHeight }}>
            {renderChart()}
          </div>
        ) : (
          <ResponsiveContainer 
            width="100%" 
            height="100%" 
            aspect={undefined} 
            minWidth={0} 
            minHeight={0}
            debounce={100}
            onResize={() => {
              // If dimensions become invalid, switch to fallback
              setTimeout(() => {
                const container = containerRef.current;
                if (container) {
                  const rect = container.getBoundingClientRect();
                  if (rect.width <= 0 || rect.height <= 0) {
                    setUseFallback(true);
                  }
                }
              }, 0);
            }}
          >
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>

    </div>
  );
};

export default GraphRenderer;
